import type { ClinicList } from '../hooks/useClinicDirectory';

/** Shown in place of the dropdown's own guidance when the directory could not be read. */
const fallbackHint = 'The clinic list could not be loaded, so type the name instead — spell it as the clinic does.';

/**
 * A clinic name, chosen from `conf.Clinics` — the directory another system maintains and this one
 * only reads. One component for every form that asks which clinic, so a candidate, an observer and
 * an employee are all offered the same list and none of them is typing a name into a free text box
 * that the API will then refuse.
 *
 * Degrades to a text box when the directory cannot be read, rather than offering a dropdown with
 * nothing in it: the form is the person's to send, and an outage on somebody else's table is not a
 * reason to stop them. Every endpoint checking a submitted clinic name stands its check down in
 * exactly that case, so the two ends agree about what is acceptable.
 *
 * Three parts and no more — label, control, messages — because `.field` is a subgrid of exactly
 * three rows and a fourth child lands in a row the parent never sized, drawn on top of the third.
 * Both messages therefore share one box.
 */
export function ClinicField({
  id,
  label,
  clinics,
  value,
  errors,
  hint,
  optional,
  maxLength = 200,
  onChange,
}: {
  id: string;
  label: string;
  clinics: ClinicList;
  value: string;
  errors?: string[];

  /** The field's own guidance. Kept alongside the fallback note, which answers a different question. */
  hint?: string;

  /** Marked in words rather than by the absence of an asterisk, which nobody reads as a signal. */
  optional?: boolean;

  /** What the API will accept, for the text box the unavailable state falls back to. */
  maxLength?: number;
  onChange: (value: string) => void;
}) {
  const invalid = (errors?.length ?? 0) > 0;
  const unavailable = clinics.state === 'unavailable';

  const describedBy = [
    hint ? `${id}-hint` : null,
    unavailable ? `${id}-fallback` : null,
    invalid ? `${id}-error` : null,
  ]
    .filter(Boolean)
    .join(' ');

  // A clinic that has closed since the form was filled in is off the directory's list but still on
  // the page. It stays offered, last, so somebody editing one answer is not silently moved to
  // another clinic by a dropdown that cannot show the one they named.
  const options =
    clinics.state !== 'ready'
      ? []
      : value !== '' && !clinics.names.includes(value)
        ? [...clinics.names, value]
        : clinics.names;

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {optional ? (
          <span className="field__optional">(optional)</span>
        ) : (
          /* Hidden from the reading because `required` below says the same thing in the one way a
             screen reader states it anyway — an asterisk read out as "star" is noise, and read out
             as nothing at all, which is the other common handling, is a required field that never
             announced itself. */
          <span className="field__required" aria-hidden="true">
            *
          </span>
        )}
      </label>

      {unavailable ? (
        <input
          id={id}
          className={invalid ? 'control control--invalid' : 'control'}
          type="text"
          value={value}
          maxLength={maxLength}
          required={!optional}
          placeholder="Type the clinic name"
          aria-invalid={invalid}
          aria-describedby={describedBy.length > 0 ? describedBy : undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <select
          id={id}
          className={invalid ? 'control control--invalid' : 'control'}
          value={value}
          required={!optional}
          // Transient and it starts that way, so nothing is taken from under a cursor that had
          // already landed: the control is disabled from the first render until there is something
          // in it to choose.
          disabled={clinics.state === 'loading'}
          aria-invalid={invalid}
          aria-describedby={describedBy.length > 0 ? describedBy : undefined}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">{clinics.state === 'loading' ? 'Loading clinics…' : 'Choose a clinic…'}</option>
          {options.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      )}

      {(hint || unavailable || invalid) && (
        <div className="field__messages">
          {hint && (
            <p className="field__help" id={`${id}-hint`}>
              {hint}
            </p>
          )}
          {unavailable && (
            <p className="field__help" id={`${id}-fallback`}>
              {fallbackHint}
            </p>
          )}
          {invalid && (
            <p className="field__error" id={`${id}-error`}>
              {errors?.join(' ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
